<template>
  <div class="dual-comparison">
    <div class="comparison-header">
      <h2>📊 Capture Analysis</h2>
      <p class="header-subtitle">{{ branchName }}</p>
    </div>

    <!-- Summary Cards -->
    <div class="summary-grid">
      <!-- Drift Summary (What's New) -->
      <div class="summary-card drift">
        <div class="card-header">
          <h3>🆕 What's New</h3>
          <span class="card-subtitle">(vs last merge to main)</span>
        </div>

        <div class="card-stats">
          <div class="stat-row">
            <span class="stat-icon">✓</span>
            <span class="stat-label">Unchanged:</span>
            <span class="stat-value">{{
              driftData.summary.unchanged || 0
            }}</span>
          </div>
          <div class="stat-row highlight" v-if="driftData.summary.modified > 0">
            <span class="stat-icon">📝</span>
            <span class="stat-label">Modified:</span>
            <span class="stat-value">{{ driftData.summary.modified }}</span>
          </div>
          <div class="stat-row highlight" v-if="driftData.summary.added > 0">
            <span class="stat-icon">➕</span>
            <span class="stat-label">Added:</span>
            <span class="stat-value">{{ driftData.summary.added }}</span>
          </div>
          <div class="stat-row highlight" v-if="driftData.summary.deleted > 0">
            <span class="stat-icon">🗑️</span>
            <span class="stat-label">Deleted:</span>
            <span class="stat-value">{{ driftData.summary.deleted }}</span>
          </div>
        </div>

        <button
          class="card-action-btn"
          @click="
            expandedSection = expandedSection === 'drift' ? null : 'drift'
          "
        >
          {{ expandedSection === "drift" ? "Hide" : "View" }} Drift Details
        </button>
      </div>

      <!-- Baseline Compliance Summary -->
      <div class="summary-card compliance">
        <div class="card-header">
          <h3>⚠️ Baseline Compliance</h3>
          <span class="card-subtitle">(vs recommended config)</span>
        </div>

        <div class="card-stats">
          <div class="stat-row">
            <span class="stat-icon">✓</span>
            <span class="stat-label">Compliant:</span>
            <span class="stat-value">{{
              complianceData.compliantCount || 0
            }}</span>
          </div>
          <div
            class="stat-row critical"
            v-if="complianceData.criticalCount > 0"
          >
            <span class="stat-icon">🔴</span>
            <span class="stat-label">Critical:</span>
            <span class="stat-value">{{ complianceData.criticalCount }}</span>
          </div>
          <div class="stat-row warning" v-if="complianceData.highCount > 0">
            <span class="stat-icon">🟠</span>
            <span class="stat-label">High:</span>
            <span class="stat-value">{{ complianceData.highCount }}</span>
          </div>
          <div class="stat-row info" v-if="complianceData.mediumCount > 0">
            <span class="stat-icon">🟡</span>
            <span class="stat-label">Medium:</span>
            <span class="stat-value">{{ complianceData.mediumCount }}</span>
          </div>
        </div>
        <button
          :class="[
            'filter-btn',
            { active: complianceFilter === 'prerequisite' },
          ]"
          @click="complianceFilter = 'prerequisite'"
        >
          Prerequisites ({{
            filteredComplianceDeviations("prerequisite").length
          }})
        </button>
        <button
          class="card-action-btn"
          @click="
            expandedSection =
              expandedSection === 'compliance' ? null : 'compliance'
          "
        >
          {{ expandedSection === "compliance" ? "Hide" : "View" }} Compliance
          Details
        </button>
      </div>
    </div>

    <!-- Expanded Drift Details -->
    <transition name="expand">
      <div v-if="expandedSection === 'drift'" class="details-section">
        <div class="details-header">
          <h3>📝 Changed Policies</h3>
          <div class="filter-buttons">
            <button
              :class="['filter-btn', { active: driftFilter === 'all' }]"
              @click="driftFilter = 'all'"
            >
              All ({{ driftData.changes.length }})
            </button>
            <button
              :class="['filter-btn', { active: driftFilter === 'modified' }]"
              @click="driftFilter = 'modified'"
            >
              Modified ({{ filteredDriftChanges("edit").length }})
            </button>
            <button
              :class="['filter-btn', { active: driftFilter === 'added' }]"
              @click="driftFilter = 'added'"
            >
              Added ({{ filteredDriftChanges("add").length }})
            </button>
            <button
              :class="['filter-btn', { active: driftFilter === 'deleted' }]"
              @click="driftFilter = 'deleted'"
            >
              Deleted ({{ filteredDriftChanges("delete").length }})
            </button>
          </div>
        </div>

        <!-- Group by folder -->
        <div
          v-for="(changes, folder) in groupedDriftChanges"
          :key="folder"
          class="folder-group"
        >
          <div class="folder-header" @click="toggleFolder('drift', folder)">
            <img
              v-if="getCategoryIcon(folder)"
              :src="getCategoryIcon(folder)"
              class="folder-icon-img"
              :alt="folder"
            />
            <span v-else class="folder-icon">📁</span>
            <strong>{{ folder }}</strong>
            <span class="change-count"
              >{{ changes.length }} change{{
                changes.length !== 1 ? "s" : ""
              }}</span
            >
            <span
              class="chevron"
              :class="{ expanded: expandedFolders.drift?.has(folder) }"
              >▼</span
            >
          </div>

          <transition name="expand">
            <div
              v-if="expandedFolders.drift?.has(folder)"
              class="folder-changes"
            >
              <div
                v-for="change in changes"
                :key="change.item.path"
                :class="['change-item', change.changeType]"
              >
                <div class="change-header">
                  <span class="change-icon">{{
                    getChangeIcon(change.changeType)
                  }}</span>
                  <span class="policy-name">{{
                    getFileName(change.item.path)
                  }}</span>
                  <span class="change-badge" :class="change.changeType">
                    {{ change.changeType }}
                  </span>
                  <button
                    class="btn-view-details"
                    @click="viewPolicyDetails(change, 'drift')"
                  >
                    View Details
                  </button>
                  <button
                    class="btn-mark-reviewed"
                    :class="{ reviewed: isReviewed(change.item.path) }"
                    @click="markReviewed(change.item.path)"
                  >
                    {{
                      isReviewed(change.item.path)
                        ? "✓ Reviewed"
                        : "Mark Reviewed"
                    }}
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>

    <!-- Expanded Compliance Details -->
    <transition name="expand">
      <div v-if="expandedSection === 'compliance'" class="details-section">
        <div class="details-header">
          <h3>⚠️ Baseline Deviations</h3>
          <div class="filter-buttons">
            <button
              :class="['filter-btn', { active: complianceFilter === 'all' }]"
              @click="complianceFilter = 'all'"
            >
              All ({{ complianceData.deviations?.length || 0 }})
            </button>
            <button
              :class="[
                'filter-btn',
                { active: complianceFilter === 'critical' },
              ]"
              @click="complianceFilter = 'critical'"
            >
              Critical ({{ filteredComplianceDeviations("critical").length }})
            </button>
            <button
              :class="['filter-btn', { active: complianceFilter === 'high' }]"
              @click="complianceFilter = 'high'"
            >
              High ({{ filteredComplianceDeviations("high").length }})
            </button>
            <button
              :class="['filter-btn', { active: complianceFilter === 'medium' }]"
              @click="complianceFilter = 'medium'"
            >
              Medium ({{ filteredComplianceDeviations("medium").length }})
            </button>
          </div>
        </div>

        <!-- Group by category -->
        <div
          v-for="(deviations, category) in groupedComplianceDeviations"
          :key="category"
          class="folder-group"
        >
          <div
            class="folder-header"
            @click="toggleFolder('compliance', category)"
          >
            <img
              v-if="getCategoryIcon(category)"
              :src="getCategoryIcon(category)"
              class="folder-icon-img"
              :alt="category"
            />
            <span v-else class="folder-icon">📁</span>
            <strong>{{ category }}</strong>
            <span class="change-count"
              >{{ deviations.length }} deviation{{
                deviations.length !== 1 ? "s" : ""
              }}</span
            >
            <span
              class="chevron"
              :class="{ expanded: expandedFolders.compliance?.has(category) }"
              >▼</span
            >
          </div>

          <transition name="expand">
            <div
              v-if="expandedFolders.compliance?.has(category)"
              class="folder-changes"
            >
              <div
                v-for="deviation in deviations"
                :key="deviation.path"
                :class="['change-item', 'deviation', deviation.severity]"
              >
                <div class="change-header">
                  <span class="severity-indicator" :class="deviation.severity">
                    {{ deviation.severity }}
                  </span>
                  <span class="policy-name">{{ deviation.policyName }}</span>
                  <span class="deviation-count"
                    >{{ deviation.count }} properties</span
                  >
                  <button
                    class="btn-view-details"
                    @click="viewPolicyDetails(deviation, 'compliance')"
                  >
                    View Details
                  </button>
                  <button
                    class="btn-mark-reviewed"
                    :class="{ reviewed: isReviewed(deviation.path) }"
                    @click="markReviewed(deviation.path)"
                  >
                    {{
                      isReviewed(deviation.path)
                        ? "✓ Reviewed"
                        : "Mark Reviewed"
                    }}
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "DualComparison",

  props: {
    driftData: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    clientCode: {
      type: String,
      required: true,
    },
    branchName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      expandedSection: null, // 'drift' or 'compliance'
      driftFilter: "all",
      complianceFilter: "all",
      expandedFolders: {
        drift: new Set(),
        compliance: new Set(),
      },
      reviewedPolicies: new Set(),
    };
  },

  computed: {
    groupedDriftChanges() {
      const filtered =
        this.driftFilter === "all"
          ? this.driftData.changes
          : this.driftData.changes.filter(
              (c) => c.changeType === this.driftFilter
            );

      return this.groupByFolder(filtered);
    },

    categoryIcons() {
      return {
        AzureAD: "entra.png",
        AzureMaintenance: "AzureMaintenance.png",
        AzurePolicy: "AzurePolicy.png",
        Defender: "Defender.png",
        Exchange: "Exchange.png",
        Forms: "Forms.png",
        Intune: "Intune.png",
        Planner: "Planner.png",
        SharePoint: "Sharepoint.png",
        Teams: "Teams.png",
        "Conditional Access": "entra.png",
        "Azure AD Settings": "entra.png",
        "Device Settings": "Intune.png",
        "Defender for Office 365": "Defender.png",
        "Exchange Security": "Exchange.png",
      };
    },

    groupedComplianceDeviations() {
      if (!this.complianceData.deviations) return {};

      const filtered =
        this.complianceFilter === "all"
          ? this.complianceData.deviations
          : this.complianceData.deviations.filter(
              (d) => d.severity === this.complianceFilter
            );

      return this.groupByCategory(filtered);
    },
  },

  methods: {
    filteredDriftChanges(type) {
      return this.driftData.changes.filter((c) => c.changeType === type);
    },

    getCategoryIcon(categoryName) {
      const iconFilename = this.categoryIcons[categoryName];
      if (iconFilename) {
        return require(`@/assets/${iconFilename}`);
      }
      return null; // Return null if no icon found
    },

    filteredComplianceDeviations(severity) {
      return (this.complianceData.deviations || []).filter(
        (d) => d.severity === severity
      );
    },

    groupByFolder(changes) {
      const groups = {};
      changes.forEach((change) => {
        const folder = this.getFolderFromPath(change.item.path);
        if (!groups[folder]) groups[folder] = [];
        groups[folder].push(change);
      });
      return groups;
    },

    groupByCategory(deviations) {
      const groups = {};
      deviations.forEach((deviation) => {
        const category = deviation.category || "Other";
        if (!groups[category]) groups[category] = [];
        groups[category].push(deviation);
      });
      return groups;
    },

    getFolderFromPath(path) {
      const parts = path.split("/").filter((p) => p);
      if (parts.length > 1) {
        return parts.slice(-2, -1)[0]; // Second to last part
      }
      return "Root";
    },

    getFileName(path) {
      return path.split("/").pop().replace(".json", "");
    },

    getChangeIcon(changeType) {
      const icons = { add: "➕", edit: "📝", delete: "🗑️" };
      return icons[changeType] || "📄";
    },

    toggleFolder(section, folder) {
      if (!this.expandedFolders[section]) {
        this.expandedFolders[section] = new Set();
      }

      if (this.expandedFolders[section].has(folder)) {
        this.expandedFolders[section].delete(folder);
      } else {
        this.expandedFolders[section].add(folder);
      }

      this.$forceUpdate();
    },

    viewPolicyDetails(item, type) {
      if (type === "compliance") {
        // For compliance deviations, pass the complete deviation object
        this.$emit("view-details", {
          path: item.path,
          policyName: item.policyName,
          category: item.category,
          severity: item.severity,
          deviationCount: item.count,
          deviations: item.deviations, // This contains all the property-level changes
          type: "compliance",
        });
      } else {
        // For drift changes
        this.$emit("view-details", {
          item,
          type: "drift",
        });
      }
    },

    markReviewed(identifier) {
      this.$emit("mark-reviewed", identifier);
      this.reviewedPolicies.add(identifier);
      this.$forceUpdate();
    },

    isReviewed(identifier) {
      return this.reviewedPolicies.has(identifier);
    },
  },
};
</script>

<style scoped>
.dual-comparison {
  background: white;
}

.comparison-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.comparison-header h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #666;
  font-family: "Courier New", monospace;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 24px;
}

.summary-card {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s;
}

.summary-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-card.drift {
  border-left: 4px solid #007bff;
}

.summary-card.compliance {
  border-left: 4px solid #ffc107;
}

.card-header {
  margin-bottom: 16px;
}

.card-header h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.card-subtitle {
  font-size: 12px;
  color: #666;
}

.card-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  padding: 8px;
  border-radius: 4px;
}

.stat-row.highlight {
  background: #fff3cd;
}

.stat-row.critical {
  background: #f8d7da;
  font-weight: 600;
}

.stat-row.warning {
  background: #fff3cd;
}

.stat-row.info {
  background: #d1ecf1;
}

.stat-icon {
  font-size: 16px;
}

.stat-label {
  color: #666;
}

.stat-value {
  margin-left: auto;
  font-weight: 600;
  color: #1a1a1a;
}

.card-action-btn {
  width: 100%;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #007bff;
  cursor: pointer;
  transition: all 0.2s;
}

.card-action-btn:hover {
  background: #e9ecef;
  border-color: #007bff;
}

.details-section {
  padding: 24px;
  border-top: 2px solid #e0e0e0;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.details-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.filter-buttons {
  display: flex;
  gap: 8px;
}

.filter-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  border-color: #007bff;
}

.filter-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.folder-group {
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.folder-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s;
}

.folder-header:hover {
  background: #e9ecef;
}

.folder-icon {
  font-size: 16px;
}

.change-count,
.deviation-count {
  margin-left: auto;
  padding: 2px 8px;
  background: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
}

.chevron {
  font-size: 10px;
  transition: transform 0.3s;
}

.chevron.expanded {
  transform: rotate(180deg);
}

.folder-changes {
  padding: 8px;
}

.change-item {
  padding: 12px;
  margin-bottom: 8px;
  border-left: 4px solid #e0e0e0;
  border-radius: 4px;
  background: #f8f9fa;
}

.change-item.add {
  border-left-color: #28a745;
}
.change-item.edit {
  border-left-color: #ffc107;
}
.change-item.delete {
  border-left-color: #dc3545;
}
.change-item.deviation.critical {
  border-left-color: #dc3545;
}
.change-item.deviation.high {
  border-left-color: #fd7e14;
}
.change-item.deviation.medium {
  border-left-color: #ffc107;
}

.change-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.change-icon {
  font-size: 16px;
}

.policy-name {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
}

.change-badge {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.change-badge.add {
  background: #d4edda;
  color: #155724;
}
.change-badge.edit {
  background: #fff3cd;
  color: #856404;
}
.change-badge.delete {
  background: #f8d7da;
  color: #721c24;
}

.severity-indicator {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.severity-indicator.critical {
  background: #dc3545;
  color: white;
}
.severity-indicator.high {
  background: #fd7e14;
  color: white;
}
.severity-indicator.medium {
  background: #ffc107;
  color: #000;
}

.btn-view-details,
.btn-mark-reviewed {
  margin-left: auto;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-view-details {
  background: #007bff;
  color: white;
}

.btn-view-details:hover {
  background: #0056b3;
}

.btn-mark-reviewed {
  background: #6c757d;
  color: white;
}

.btn-mark-reviewed:hover {
  background: #545b62;
}

.btn-mark-reviewed.reviewed {
  background: #28a745;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 5000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.folder-icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.folder-icon {
  font-size: 16px;
}
</style>
